export interface RemoteFile {
  url: string,
  name: string|null,
}

export interface UploadedFile extends RemoteFile {
  id: number,
  name: string,
}

export interface AppUserAccount {
  id: number,
  type: number,
  name: string,
}

export interface AppUser {
  id: number,
  account: AppUserAccount,
  displayName: string,
  firstName?: string, // Legacy
  lastName?: string, // Legacy
  role: string,
  scopes: string[],
  isClient: boolean
}

export interface Tax {
  id: number,
  locationId: number,
  name: string,
  abbrev: string,
  rate: string,
}

export const AuthType = {
  OAUTH: 'OAUTH',
  LOGIN: 'LOGIN',
};

export interface Auth {
  type: 'OAUTH',
  version: string|null
}

export interface SocialSite {
  id: number,
  type: string,
  name: string,
  auth: Auth|null,
  status: number,
  importPosts: boolean
}

export interface CollectionFilters {
  orderBy?: string,
  order?: 'asc'|'desc',
  offset?: number,
  limit?: number,
}

export interface Role {
  id: number,
  type: string,
  name: string,
  description: string,
  position: number
}

export interface User {
  id: number,
  accountId: number|null,
  clientId: number|null,
  firstName: string,
  lastName: string,
  email: string,
  role: Role,
  status: number,
  setupReminderDisabledAt: string|null,
  createdAt: string,
}

export interface DetailedUser extends User {
  totalSubscriptions: number
}

export interface UserFilters {
  keyword?: string,
  excludeSelf?: boolean,
  offset?: number,
  limit?: number,
}

export interface UserConfig {
  importPosts: boolean
}

export interface Card {
  brand: string,
  name: string,
  last4: string,
  expMonth: number,
  expYear: number,
}

export interface PaymentMethod {
  id: number,
  customerId: number,
  card: Card,
  addedAt: string,
  label: string|null,
}

export interface Client {
  id: number,
  customerId: number,
  user: User,
  firstName: string,
  lastName: string,
  email: string,
  phone: string|null,
  dateOfBirth: string,
  fileRefNo: string,
  dateOfLoss: string,
  status: number,
  agreedTermsAt: string|null,
  socialAccounts?: SocialAccount[],
  createdAt: string,
}

export interface DetailedClient extends Client {
  totalUnreviewed: number,
  totalEscalated: number,
  totalFlagged: number,
  totalTimeline: number,
  totalReports: number,
  totalPersons: number,
  totalTrashedPersons: number,
}

export interface ClientFilters {
  offset?: number,
  limit?: number,
  keyword?: string,
  pendingReconnections?: boolean
}

export interface Charge {
  id: number,
  clientId: number,
  description: string,
  currency: string,
  subTotal: number,
  discountedSubTotal: number,
  total: number,
  chargeTaxes: ChargeTax[],
  chargedAt: string,
}

export interface ChargeTax {
  id: number,
  chargeId: number,
  tax: Tax,
  rate: string,
}

export interface ChargeFilters extends CollectionFilters {
  keyword?: string,
}

export interface SocialAccount {
  id: number,
  clientId: number,
  socialSite: SocialSite,
  auth: Auth|null,
  authenticatedAt: string,
  primarySince: string|null,
  lastModified: string
}

export interface Modal extends HTMLElement {
  show(): void,
  hide(): void,
}

export interface QueryResult<T = object> {
  total: number,
  items: T[],
}

export interface List extends HTMLElement {
  prepend(item: any): void,
  push(item: any): void,
  removeItem(item: any): void,
}


export interface UserClient {
  id: number,
  user: User,
  client: DetailedClient,
  assignedAt: string,
  starredAt: string|null,
}

export interface UserClientFilters {
  isStarred?: boolean,
  keyword?: string,
  orderBy?: string,
  order?: string,
}

export interface UserNotification {
  id: number,
  notification: Notification,
  userId: number,
  readAt: string|null,
  createdAt: string
}

export interface UserNotificationFilter {
  isRead?: boolean,
  offset?: number,
  limit?: number,
}

export interface Notification {
  id: number,
  type: string,
  data: [],
  occurredAt: string,
}

export interface PostImport {
  id: number,
  accountId: number,
  jobId: number,
  createdAt: string
}

export interface PostImportFilters extends CollectionFilters {
  source: string,
  status?: 'success'
}

export interface PostNote {
  id: number,
  postId: number,
  user: User|null,
  content: string,
  addedAt: string
}

export interface PostGroup {
  id: number,
  type: number,
  clientId: number,
  title: string,
  content: string|null,
  link: string,
  postedAt: string,
}

export interface Media {
  type: 'image'|'video'|'embed',
  url: string,
  thumbnail: string|null,
  embedHtml: string|null,
}

export interface Post {
  id: number,
  type: number,
  parent: PostGroup|null,
  client: Client,
  socialSite: SocialSite,
  title: string,
  content: string|null,
  media: Media|null,
  link: string,
  isPrivate: boolean,
  flaggedAt: string|null,
  flaggedWords: string[]|null,
  postedAt: string,
  status: number,
  lifecycleStatus: LifecycleStatus|null,
  movedToTimelineAt: string|null,
  isTrashed: boolean,
  totalNotes: number,
  totalFaces: number,
}

export interface PostCategory {
  id: number,
  platformId: number,
  type: string,
  name: string,
}

export interface PostFilters extends CollectionFilters {
  accountIds?: number[],
  clientIds?: number[],
  parentId?: number,
  statuses: number[],
  isOnTimeline?: boolean,
  isInclusive?: boolean,
  socialSiteIds?: number[],
  since?: string|null,
  until?: string|null,
  isTrashed?: boolean,
  isPrivate?: boolean,
  isFlagged?: boolean,
  lifecycleStatuses?: string[],
  mediaTypes?: string[],
  categoryIds?: PostPlatformCategoryFilter,
  personIds?: number[],
  labelIds?: number[],
  labelCategoryIds?: number[],
  excludedLabelIds?: number[],
}

export interface PostDateRange {
  since?: string|null,
  until?: string|null
}

export interface Job {
  id: string
  createdAt: string,
}

export interface PostSource {
  id: number,
  socialSiteId: number,
  type: 'file',
  name: string,
  createdAt: string
}

export interface PostSourceFilters {
  socialSiteId?: number,
  type?: 'file'
}

export interface PostPlatformCategoryFilter{
  [i: number]: {
    all?: boolean;
    categoryId: number[];
  }
}

export enum LifecycleStatus {
  ARCHIVED = 'archived',
  TRASHED = 'trashed',
}

export interface PostSearch {
  id: number,
  client: Client,
  keywords: string[],
  job: Job,
  numPostsMatched: number,
  createdAt: string
}

export interface PostSearchFilters extends CollectionFilters {
  tags?: string[]
}

export enum PostSearchMatchType{
  CONTENT = 'content',
  TRANSCRIPT = 'transcript'
}

export interface PostSearchMatch {
  id: number,
  post: Post,
  type: PostSearchMatchType,
  keywords: string[],
}

export interface PostSearchMatchFilters extends CollectionFilters {

}

export interface MonitorWord {
  id: number,
  word: number,
  accountId: number|null,
  clientId: number|null,
  createdAt: string,
}

export interface MonitorWordFilters extends CollectionFilters {
  accountId?: number,
  clientId?: number
}

export interface Select extends HTMLElement {
  selectAll(): void,
}

export interface DatePicker {
  open(): void,
  clear(): void,
  setDate(value: string, trigger: Boolean): void,
}

export interface DateRange {
  start: string,
  end: string,
}

export interface OpenDateRange {
  start?: string|null,
  end?: string|null,
}

export interface DateRangePickerElement extends HTMLElement{
  clear(): void,
}

export interface Report {
  id: number,
  client: Client,
  type: string,
  name: string,
  statuses: number[],
  source: string|null,
  isOnTimeline: boolean,
  isFlagged: boolean|null,
  socialSiteIds: number[],
  personIds: number[],
  startDate: string|null,
  endDate: string|null,
  createdAt: string,
  lastModified: string
}

export interface ReportFormatOptions {
  branding: boolean,
  index: boolean,
  internalNotes: boolean,
  externalComments: boolean,
  footer: boolean,
  timeZone?: string
}

export interface ReportFilters extends CollectionFilters {
  types: string[],
  name?: string,
  isFinalized?: boolean,
  personIds?: number[],
}

export interface ReportPost {
  id: number,
  reportId: number,
  post: Post,
  comment: string|null,
  isTrashed: boolean,
}

export interface ReportPostFilters extends CollectionFilters {

}

export interface Rules {
  accountTypes?: number[],
  scope?: string,
  check?(...args: any[]): boolean,
}

export interface ActionRules {
  [action: string]: Rules,
}

export interface QTablePagination {
  page: number,
  rowsPerPage: number,
  rowsNumber: number,
  sortBy?: string,
  descending?: boolean
}

export type JobStatus = 'RUNNING'|'SUCCESS'|'FAILURE';

export interface JobRun {
  id: number,
  jobId: number,
  startedAt: string,
  finishedAt: string|null,
  status: JobStatus
}

export interface AccountStats {
  pendingReconnections: number,
}

export interface PostTag {
  id: number,
  resourceType: string,
  resourceId: number,
  post: Post|null,
  createdAt: string,
}

export interface PostTagFilters extends CollectionFilters {
  postId?: number,
  resourceTypes?: string[],
  resourceIds?: number[],
  resources?: Record<string, number[]>,
}
